/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Button, Collapse, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { useEffect, useRef, useState } from "react";

import TestIds from "../../testIds";

import styles from "./ExpandableContent.module.scss";

export interface IExpandableContentProps {
  title: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

export const ExpandableContent = ({
  title,
  children,
  defaultExpanded = false,
}: IExpandableContentProps) => {
  const titleRef = useRef<HTMLSpanElement>(null);

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [titleFontStyles, setTitleFontStyles] = useState<SxProps<Theme>>({
    fontSize: "1rem",
    color: "inherit",
  });

  useEffect(() => {
    if (titleRef.current) {
      const computedStyle = window.getComputedStyle(
        titleRef.current.firstElementChild || titleRef.current
      );
      setTitleFontStyles({
        fontSize: computedStyle.fontSize,
        color: computedStyle.color,
      });
    }
  }, [title]);

  return (
    <>
      <Button
        variant="farmlands2.0_typography-small"
        color="secondary"
        onClick={() => setExpanded(!expanded)}
        data-testid={TestIds.components.expandableContent.toggleButton}
        className={styles.expandableContent__toggleButton}
      >
        <span ref={titleRef}>{title}</span>

        <Typography
          data-testid={TestIds.components.expandableContent.toggleButtonIcon}
          variant="materialIcon"
          component="span"
          sx={{
            transition: "all 0.2s linear",
            transform: expanded ? "scaleY(-1)" : "scaleY(1)",
            marginLeft: "8px",
            ...titleFontStyles,
          }}
          aria-hidden="true"
        >
          expand_more
        </Typography>
      </Button>

      <Collapse
        in={expanded}
        data-testid={TestIds.components.expandableContent.collapseContent}
        sx={{ alignSelf: "stretch" }}
      >
        {children}
      </Collapse>
    </>
  );
};
